import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { fetchGameConfig } from '../lib/firebase'
import { localeAwareUpperCase } from '../lib/words'

interface GameContextType {
  solution: string | null
  solutionGameDate: Date
  tomorrow: number
  isLoading: boolean
  error: Error | null
  isLocked: boolean
}

const GameContext = createContext<GameContextType | undefined>(undefined)

export const GameProvider = ({ children }: { children: ReactNode }) => {
  const [gameState, setGameState] = useState<GameContextType>({
    solution: null,
    solutionGameDate: new Date(),
    tomorrow: Date.now() + 86400000,
    isLoading: true,
    error: null,
    isLocked: false,
  })

  useEffect(() => {
    const loadSolution = async () => {
      try {
        const config = await fetchGameConfig()

        // Check if scheduled word should activate
        if (config.nextWord && config.nextWordScheduledFor) {
          const scheduledDate = new Date(config.nextWordScheduledFor)
          if (new Date() >= scheduledDate) {
            // Use next word instead
            setGameState({
              solution: localeAwareUpperCase(config.nextWord),
              solutionGameDate: new Date(),
              tomorrow: Date.now() + 86400000,
              isLoading: false,
              error: null,
              isLocked: config.isLocked ?? false,
            })
            return
          }
        }

        setGameState({
          solution: localeAwareUpperCase(config.currentWord),
          solutionGameDate: new Date(config.currentWordDate),
          tomorrow: Date.now() + 86400000,
          isLoading: false,
          error: null,
          isLocked: config.isLocked ?? false,
        })
      } catch (error) {
        console.error('Failed to load word:', error)
        // Fallback to hardcoded word
        setGameState({
          solution: 'CHAMP',
          solutionGameDate: new Date(),
          tomorrow: Date.now() + 86400000,
          isLoading: false,
          error: error as Error,
          isLocked: false,
        })
      }
    }

    loadSolution()
  }, [])

  return <GameContext.Provider value={gameState}>{children}</GameContext.Provider>
}

export const useGame = () => {
  const context = useContext(GameContext)
  if (!context) {
    throw new Error('useGame must be used within GameProvider')
  }
  return context
}
